/**
 * MEASUREMENT ONLY - no behaviour change. Task 24731.
 *
 * Captures per-request latency and pairs it with the backend's Server-Timing header, so we
 * can see where the fixed ~850ms of per-request overhead measured on production is actually
 * spent. Silent unless explicitly switched on.
 *
 * Usage, in the browser console:
 *
 *   localStorage.apiTiming = '1'   // then reload
 *   ...click around the slow screens...
 *   dumpApiTimings()               // per-endpoint table
 *   dumpApiTimings({ raw: true })  // every sample
 *   clearApiTimings()
 *
 * Samples are always collected into window.__apiTimings (cheap, capped) so you can enable
 * the flag, reload, and still call dumpApiTimings() without it. The flag only controls the
 * noisy per-request console.debug line.
 *
 * To remove: delete this file and the recordTiming/isTimingEnabled calls in fetchAPI.js
 * and api_helper.js.
 */

const MAX_SAMPLES = 2000;

/** Phases emitted by App\Filters\ServerTimingFilter, in the order they occur. */
export const TIMING_PHASES = [
  "bootstrap",
  "routes",
  "dispatch",
  "filters",
  "controller",
  "total",
];

export const isTimingEnabled = () => {
  try {
    return localStorage.getItem("apiTiming") === "1";
  } catch (_) {
    return false;
  }
};

/**
 * Parse a Server-Timing header into { bootstrap, routes, ... } in ms.
 * Returns an object of nulls when the header is absent, which is the signal that the
 * backend instrumentation has not been deployed to that host yet.
 */
export const parseServerTiming = (headerValue) => {
  const out = {};
  TIMING_PHASES.forEach((p) => {
    out[p] = null;
  });
  if (!headerValue || typeof headerValue !== "string") return out;

  headerValue.split(",").forEach((part) => {
    // e.g. "bootstrap;dur=12.3"
    const [name, ...params] = part.split(";").map((x) => x.trim());
    const durParam = params.find((p) => p.startsWith("dur="));
    if (!name || !durParam) return;
    const value = parseFloat(durParam.slice(4));
    if (!Number.isNaN(value)) out[name] = value;
  });

  return out;
};

/**
 * Group samples by endpoint. Strips the query string and any trailing numeric id segments
 * so /job/data/123 and /job/data/456 land in the same bucket.
 */
const endpointLabel = (url) => {
  if (!url) return "(unknown)";
  let label = String(url).split("?")[0];
  label = label.replace(/\/\d+(?=\/|$)/g, "/:id");
  return label;
};

export const recordTiming = ({ url, method, perceivedMs, server, status }) => {
  try {
    if (!window.__apiTimings) window.__apiTimings = [];
    const samples = window.__apiTimings;

    const sample = {
      endpoint: endpointLabel(url),
      url,
      method: method || "GET",
      status: status ?? null,
      perceivedMs: Math.round(perceivedMs * 10) / 10,
      ...(server || parseServerTiming(null)),
      at: Date.now(),
    };

    // network = what the round trip cost outside PHP. Null when the header is missing.
    sample.networkMs =
      typeof sample.total === "number"
        ? Math.round((sample.perceivedMs - sample.total) * 10) / 10
        : null;

    samples.push(sample);
    if (samples.length > MAX_SAMPLES) samples.splice(0, samples.length - MAX_SAMPLES);

    if (isTimingEnabled()) {
      const s = sample;
      // eslint-disable-next-line no-console
      console.debug(
        `[apiTiming] ${s.method} ${s.endpoint} perceived=${s.perceivedMs}ms` +
          (s.total === null
            ? " (no Server-Timing header - backend not instrumented on this host)"
            : ` server=${s.total}ms network=${s.networkMs}ms` +
              ` (bootstrap=${s.bootstrap} routes=${s.routes} dispatch=${s.dispatch}` +
              ` filters=${s.filters} controller=${s.controller})`)
      );
    }
  } catch (_) {
    // Instrumentation must never break a request.
  }
};

const percentile = (sortedValues, p) => {
  if (!sortedValues.length) return null;
  const idx = Math.min(
    sortedValues.length - 1,
    Math.max(0, Math.ceil((p / 100) * sortedValues.length) - 1)
  );
  return Math.round(sortedValues[idx] * 10) / 10;
};

const avg = (values) => {
  const nums = values.filter((v) => typeof v === "number");
  if (!nums.length) return null;
  return Math.round((nums.reduce((a, b) => a + b, 0) / nums.length) * 10) / 10;
};

/**
 * Aggregate the captured samples per endpoint and print a console.table.
 * This is the deliverable: click around, then read the table.
 */
export const dumpApiTimings = (options = {}) => {
  const samples = window.__apiTimings || [];
  if (!samples.length) {
    // eslint-disable-next-line no-console
    console.log(
      "[apiTiming] no samples captured yet. Navigate the app, then run dumpApiTimings() again."
    );
    return [];
  }

  if (options.raw) {
    // eslint-disable-next-line no-console
    console.table(samples);
    return samples;
  }

  const byEndpoint = new Map();
  samples.forEach((s) => {
    const key = `${s.method} ${s.endpoint}`;
    if (!byEndpoint.has(key)) byEndpoint.set(key, []);
    byEndpoint.get(key).push(s);
  });

  const rows = [];
  byEndpoint.forEach((group, key) => {
    const perceived = group.map((s) => s.perceivedMs).sort((a, b) => a - b);
    rows.push({
      endpoint: key,
      count: group.length,
      p50: percentile(perceived, 50),
      p95: percentile(perceived, 95),
      max: percentile(perceived, 100),
      network: avg(group.map((s) => s.networkMs)),
      bootstrap: avg(group.map((s) => s.bootstrap)),
      routes: avg(group.map((s) => s.routes)),
      dispatch: avg(group.map((s) => s.dispatch)),
      filters: avg(group.map((s) => s.filters)),
      controller: avg(group.map((s) => s.controller)),
      server: avg(group.map((s) => s.total)),
    });
  });

  // Worst total time first: that is where the attention should go.
  rows.sort((a, b) => b.p50 * b.count - a.p50 * a.count);

  const totalRequests = samples.length;
  const wallMs = samples.reduce((a, s) => a + s.perceivedMs, 0);
  // eslint-disable-next-line no-console
  console.log(
    `[apiTiming] ${totalRequests} requests, ${Math.round(wallMs)}ms of cumulative request time.` +
      ` Sorted by p50 x count (total cost), worst first.`
  );
  // eslint-disable-next-line no-console
  console.table(rows);
  return rows;
};

export const clearApiTimings = () => {
  window.__apiTimings = [];
  // eslint-disable-next-line no-console
  console.log("[apiTiming] cleared");
};

// Expose on window so they can be called straight from the console with no import.
try {
  if (typeof window !== "undefined") {
    window.dumpApiTimings = dumpApiTimings;
    window.clearApiTimings = clearApiTimings;
  }
} catch (_) {
  // ignore
}
